import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "activator",
    "checkbox",
    "declineButtonText",
    "prompt",
    "saveCheckbox",
    "terms",
  ]
  static values = {
    submitDeclineButtonText: { type: String, default: "No, just pay once" },
  }

  connect() {
    this.agreed = this.hasCheckboxTarget ? this.checkboxTarget.checked : true
    this.bypassSaveOnlyPrompt = false
    this.submitAfterAgreement = false
  }

  saveChanged() {
    if (!this.hasSaveCheckboxTarget) return

    if (!this.saveCheckboxTarget.checked) {
      if (this.hasCheckboxTarget) this.checkboxTarget.checked = false
      this.agreed = false
    }
  }

  requestAgreement() {
    if (!this.hasCheckboxTarget) return

    if (!this.checkboxTarget.checked) {
      this.agreed = false
      return
    }

    if (this.agreed) return

    this.checkboxTarget.checked = false
    this.submitAfterAgreement = false
    this.#setDeclineButtonText("Cancel")
    this.activatorTarget.click()
  }

  confirmBeforePayment(event) {
    if (this.bypassSaveOnlyPrompt || !this.#shouldPromptForAutopay()) return

    event.preventDefault()
    event.stopImmediatePropagation()

    this.pendingPaymentButton = event.submitter || event.currentTarget
    this.pendingPaymentForm = event.currentTarget?.matches("form")
      ? event.currentTarget
      : this.element.closest("form")
    this.submitAfterAgreement = true
    this.#setDeclineButtonText(this.submitDeclineButtonTextValue)
    this.activatorTarget.click()
  }

  showTerms(event) {
    event.preventDefault()
    this.promptTarget.classList.add("hidden")
    this.termsTarget.classList.remove("hidden")
  }

  agree() {
    this.agreed = true
    if (this.hasCheckboxTarget) this.checkboxTarget.checked = true
    if (this.hasSaveCheckboxTarget) this.saveCheckboxTarget.checked = true
    this.#closeDialog()
    this.#resumePaymentIfNeeded()
  }

  decline() {
    this.agreed = false
    if (this.hasCheckboxTarget) this.checkboxTarget.checked = false
    this.#closeDialog()
    this.#resumePaymentIfNeeded()
  }

  #resumePaymentIfNeeded() {
    if (!this.submitAfterAgreement) return

    const paymentButton = this.pendingPaymentButton
    const paymentForm = this.pendingPaymentForm || this.element.closest("form")

    this.bypassSaveOnlyPrompt = true
    this.pendingPaymentButton = null
    this.pendingPaymentForm = null
    this.submitAfterAgreement = false

    if (paymentButton?.isConnected && paymentButton.type !== "submit") {
      paymentButton.click()
    } else if (paymentForm?.requestSubmit) {
      if (paymentButton?.isConnected) {
        paymentForm.requestSubmit(paymentButton)
      } else {
        paymentForm.requestSubmit()
      }
    }
  }

  #setDeclineButtonText(text) {
    this.declineButtonTextTarget.textContent = text
  }

  #shouldPromptForAutopay() {
    const savingPaymentMethod = this.hasSaveCheckboxTarget
      ? this.saveCheckboxTarget.checked
      : true

    return savingPaymentMethod && this.hasCheckboxTarget && !this.checkboxTarget.checked
  }

  #closeDialog() {
    const dialogRoot = this.element.querySelector('[data-controller="dialog"]')
    if (!dialogRoot) return

    const dialogController =
      this.application.getControllerForElementAndIdentifier(dialogRoot, "dialog")
    dialogController?.close()
    this.#resetView()
  }

  #resetView() {
    this.promptTarget.classList.remove("hidden")
    this.termsTarget.classList.add("hidden")
  }
}
